/**
 * SalesFooter Component
 * 
 * Displays action buttons and modals for the Sales component including:
 * - Primary actions (Add Order, Generate Bill)
 * - Secondary actions (Reset Table/Group, Move Table)
 * - Modals (ResetTable, ChangeTable, ResetGroup, ChargedMesaWarning)
 */

import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { FaPlus, FaReceipt, FaTrashCan, FaArrowsUpDown } from 'react-icons/fa6';
import ResetTable from '../../../../../../../Configuracion/Salones/components/SalonDisplayer/components/MesaController/components/ResetTable/ResetTable';
import ChangeTable from '../../../../../../../Configuracion/Salones/components/SalonDisplayer/components/MesaController/components/ChangeTable/ChangeTable';
import ResetGroup from '../../../../../../Common/ResetGroup/ResetGroup';
import ChargedMesaWarning from './ChargedMesaWarning';
import SplitReceiptButton from '../../../../../../Common/CobrarPedidos/components/SplitReceiptButton/SplitReceiptButton';
import { getPermissionState } from '../../../../../../../../../utils/functions';
import { VIEW_MODES, MODAL_TYPES, PERMISSIONS, type ViewMode, type ModalType } from '../../utils';
import './SalesFooter.scss';

interface SalesFooterProps {
  // Selection props
  selectedMesa?: any;
  selectedGroup?: any;
  pedidos: any[];
  selections: any[];
  
  // State
  blocked: boolean;
  isSelectionMode: boolean;
  activeModal: ModalType;
  
  // Actions
  setViewMode: (mode: ViewMode) => void;
  setActiveModal: (modal: ModalType) => void;
  onRefreshPedidos: () => void;
  onClearGroupSelection?: () => void;
  onSetAutoCreateSingle?: (value: boolean) => void; // NEW: Set autoCreateSingle flag
  onSetSelectedSplitMethod?: (method: 'equal' | 'custom' | undefined) => void; // NEW: Set selected split method from dropdown
}

const SalesFooter: React.FC<SalesFooterProps> = ({
  selectedMesa,
  selectedGroup,
  pedidos,
  selections,
  blocked,
  isSelectionMode,
  activeModal,
  setViewMode,
  setActiveModal,
  onRefreshPedidos,
  onClearGroupSelection,
  onSetAutoCreateSingle,
  onSetSelectedSplitMethod,
}) => {
  // State for charged mesa warning modal
  const [showChargedWarning, setShowChargedWarning] = useState(false);

  // Footer works for both mesas and groups
  if (!selectedMesa && !selectedGroup) return null;

  const isUsed = selectedMesa?.used || (selectedGroup && pedidos.length > 0);
  const ctaText = isUsed ? 'Agregar Pedido' : 'Tomar Pedido';

  // Get mesa identifier for display
  const mesaIdentifier = selectedMesa
    ? (selectedMesa.tag && selectedMesa.tag.trim() !== '' ? selectedMesa.tag : selectedMesa.num)
    : selectedGroup?.name;

  // Handle add order click - show warning if mesa is charged
  const handleAddOrderClick = () => {
    if (selectedMesa?.charged) {
      setShowChargedWarning(true);
    } else {
      setViewMode(VIEW_MODES.ORDER);
    }
  };

  // Handle confirm from charged mesa warning
  const handleChargedWarningConfirm = () => {
    setShowChargedWarning(false);
    setViewMode(VIEW_MODES.ORDER);
  };

  return (
    <div className="sales-action-footer">
      {/* Primary CTA Button - Add Order */}
      <Button
        onClick={handleAddOrderClick}
        className="footer-cta-button footer-cta-primary"
        variant="none"
        disabled={blocked || isSelectionMode}
      >
        <FaPlus /> {ctaText}
      </Button>

      {/* Secondary CTA Button - Generate Bill (only if there are orders) */}
      {pedidos.length > 0 && getPermissionState(PERMISSIONS.GENERATE_BILL) && (
        selectedMesa?.charged ? (
          // Charged mesa - show simple "Ver Precuenta" button
          <Button
            className="footer-cta-button footer-cta-secondary"
            onClick={() => setViewMode(VIEW_MODES.PAYMENT)}
            variant="none"
            disabled={blocked || isSelectionMode}
          >
            <FaReceipt /> Ver Precuenta
          </Button>
        ) : (
          // Not charged - show split button
          <SplitReceiptButton
            onGenerateSingle={() => {
              if (onSetAutoCreateSingle) onSetAutoCreateSingle(true);
              if (onSetSelectedSplitMethod) onSetSelectedSplitMethod(undefined);
              setViewMode(VIEW_MODES.PAYMENT);
            }}
            onShowSplitOptions={(method) => {
              if (onSetAutoCreateSingle) onSetAutoCreateSingle(false);
              if (onSetSelectedSplitMethod) onSetSelectedSplitMethod(method);
              setViewMode(VIEW_MODES.PAYMENT);
            }}
            disabled={blocked || isSelectionMode}
            variant="secondary"
          />
        )
      )}

      {/* Secondary Actions Row */}
      {pedidos.length > 0 && (
        <div className="footer-secondary-actions">
          {/* Reset button - Mesa or Group */}
          {getPermissionState(PERMISSIONS.EDIT_ORDER) && !selections.some((sel: any) => sel.payed === true) && (
            <>
              {selectedMesa && (
                <Button
                  className="footer-button footer-button-danger"
                  onClick={() => setActiveModal(MODAL_TYPES.RESET)}
                  variant="outline-danger"
                  disabled={blocked || isSelectionMode}
                >
                  <FaTrashCan /> Vaciar Mesa
                </Button>
              )}
              {selectedGroup && (
                <Button
                  className="footer-button footer-button-danger"
                  onClick={() => setActiveModal(MODAL_TYPES.RESET)}
                  variant="outline-danger"
                  disabled={blocked || isSelectionMode}
                >
                  <FaTrashCan /> Vaciar Grupo
                </Button>
              )}
            </>
          )}

          {selectedMesa && (
            <Button
              className="footer-button footer-button-neutral"
              onClick={() => setActiveModal(MODAL_TYPES.MOVE)}
              variant="none"
              disabled={blocked || isSelectionMode}
            >
              <FaArrowsUpDown /> Mover Mesa
            </Button>
          )}
        </div>
      )}

      {/* Modals */}
      {pedidos.length > 0 && (
        <>
          {selectedMesa && (
            <>
              <ResetTable
                show={activeModal === MODAL_TYPES.RESET}
                onClose={() => setActiveModal(null)}
                mesa={selectedMesa}
              />

              <ChangeTable
                show={activeModal === MODAL_TYPES.MOVE}
                onClose={() => setActiveModal(null)}
                mesa={selectedMesa}
              />
            </>
          )}

          {selectedGroup && (
            <ResetGroup
              show={activeModal === MODAL_TYPES.RESET}
              onClose={() => setActiveModal(null)}
              pedidoGroup={selectedGroup}
              onSuccess={() => {
                // Refresh pedidos after successful reset
                onRefreshPedidos();
                // Clear group selection
                if (onClearGroupSelection) {
                  onClearGroupSelection();
                }
              }}
            />
          )}
        </>
      )}

      {/* Charged Mesa Warning Modal */}
      <ChargedMesaWarning
        show={showChargedWarning}
        onClose={() => setShowChargedWarning(false)}
        onConfirm={handleChargedWarningConfirm}
        mesaIdentifier={mesaIdentifier}
      />
    </div>
  );
};

export default SalesFooter;
